import { Button } from "@dropbox/dig-components/buttons";
import { FormLabel, FormRow } from "@dropbox/dig-components/form_row";
import { TextArea } from "@dropbox/dig-components/text_fields";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { AddLine } from "@dropbox/dig-icons/assets";
import { useDebounce } from "@mirage/shared/hooks/useDebounce";
import i18n from "@mirage/translations";
import { type FC, memo, useCallback, useEffect, useRef, useState } from "react";
import { useDashTeamInvitePapLogger } from "./hooks/useDashTeamInvitePapLogger";
import { type ManagedInviteFormPropsWithInitialData } from "./hooks/useManagedInviteForm";
import { InviteEntireTeam } from "./InviteEntireTeam";
import styles from "./InviteForm.module.css";
import { InviteFormRow } from "./InviteFormRow";

import { PAP_Input_DashInviteFormMessage } from "@mirage/analytics/events/types/input_dash_invite_form_message";
import { PAP_Select_DashInviteFormIndividualAdd } from "@mirage/analytics/events/types/select_dash_invite_form_individual_add";
import { PAP_Select_DashInviteFormTeamToggle } from "@mirage/analytics/events/types/select_dash_invite_form_team_toggle";
import { PAP_Shown_DashInviteForm } from "@mirage/analytics/events/types/shown_dash_invite_form";

import type { IndividualInviteFormContact, InviteFormContact } from "./types";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface InviteFormProps
  extends ManagedInviteFormPropsWithInitialData {}

export const InviteForm: FC<InviteFormProps> = memo(
  (managedInviteFormProps) => {
    const [isTextAreaFocused, setIsTextAreaFocused] = useState(false);
    const [hasInvitedEntireTeam, setHasInvitedEntireTeam] = useState(false);
    const { logDashTeamInvitePapEvent } = useDashTeamInvitePapLogger();
    const topGradientRef = useRef<HTMLDivElement>(null);
    const bottomGradientRef = useRef<HTMLDivElement>(null);

    const {
      individualContacts,
      validIndividualContactCount,
      validTeamContactCount,
      initialMessage,
      disabled,
      setTeamContacts,
      clearTeamContacts,
      addEmptyIndividualContact,
      updateIndividualContact,
      removeIndividualContact,
      updateMessage,
      initialInviteFormData: {
        teamMembers,
        licenseCount,
        licensesUsed,
        canInviteAllTeamMembers,
        canAssignRoles,
        canInviteWithOptionalMessage,
        isFssTeam,
      },
    } = managedInviteFormProps;
    const licenseToBeUsed =
      licensesUsed + validIndividualContactCount + validTeamContactCount;
    const licenseToBeUsedIfInviteEntireTeam =
      licensesUsed + validIndividualContactCount + teamMembers.length;

    // Users should not be able to create more empty typeahead fields than available licenseCount
    // then be able to fill in those fields with emails and invite more users than their license count
    // this variable will be used to gate "Add another" button so that inviters can only add empty fields up to licenseCount
    const licensesUsedWithEmptyTypeahead =
      licensesUsed + individualContacts.length + validTeamContactCount;
    /**
     * If over-quota after invite entire team checked, do not disable and allow
     *   user to fix it by unchecking
     */
    const insufficientLicenses =
      licenseToBeUsedIfInviteEntireTeam > licenseCount && !hasInvitedEntireTeam;

    const logMessageUpdate = useDebounce(
      useCallback(() => {
        logDashTeamInvitePapEvent(PAP_Input_DashInviteFormMessage, {
          eventState: "success",
        });
      }, [logDashTeamInvitePapEvent]),
      3000,
    );

    const handleMessageChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        updateMessage(e.currentTarget.value);
        logMessageUpdate();
      },
      [updateMessage, logMessageUpdate],
    );

    const handleAddRow = useCallback(() => {
      addEmptyIndividualContact();
      logDashTeamInvitePapEvent(PAP_Select_DashInviteFormIndividualAdd);
    }, [addEmptyIndividualContact, logDashTeamInvitePapEvent]);

    const handleInviteFormRowChange = useCallback(
      (contactId, contact: InviteFormContact) => {
        // TODO: Validate email
        updateIndividualContact({
          contactId,
          ...contact,
        });
      },
      [updateIndividualContact],
    );

    const handleRemoveRow = useCallback(
      (contactId: number) => {
        removeIndividualContact(contactId);
      },
      [removeIndividualContact],
    );

    const handleToggleInviteAllCheckbox = useCallback(
      (checked: boolean) => {
        if (!teamMembers) {
          return;
        }
        if (checked) {
          setTeamContacts(
            teamMembers.map((member) => ({
              email: member.email,
              newRole: null,
              validationResult: null,
            })),
          );
        } else {
          clearTeamContacts();
        }
        setHasInvitedEntireTeam(checked);
        logDashTeamInvitePapEvent(PAP_Select_DashInviteFormTeamToggle);
      },
      [
        setTeamContacts,
        clearTeamContacts,
        teamMembers,
        logDashTeamInvitePapEvent,
      ],
    );

    const getShouldDisableRoleSelect = useCallback(
      (contact: Omit<IndividualInviteFormContact, "contactId">) => {
        const hasEmail = !!contact?.email;
        const hasError = !!contact?.validationResult;
        const isTeamMember =
          teamMembers.findIndex((t) => t.email === contact.email) !== -1;
        return !hasEmail || hasError || isTeamMember;
      },
      [teamMembers],
    );

    const handleTextAreaFocus = useCallback(() => {
      setIsTextAreaFocused(true);
      logDashTeamInvitePapEvent(PAP_Input_DashInviteFormMessage);
    }, [logDashTeamInvitePapEvent]);
    const handleTextAreaBlur = useCallback(() => {
      setIsTextAreaFocused(false);
    }, []);

    const handleScrollContainerRef = useCallback(
      (el: HTMLElement | null) => {
        if (!el) return;

        const updateGradientVisibility = () => {
          const hasReachedBottom =
            Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) < 1;
          const hasReachedTop = el.scrollTop === 0;
          const shouldShowGradients = individualContacts.length > 4;

          if (topGradientRef.current) {
            topGradientRef.current.classList.toggle(
              styles.visible,
              !hasReachedTop && shouldShowGradients,
            );
          }
          if (bottomGradientRef.current) {
            bottomGradientRef.current.classList.toggle(
              styles.visible,
              !hasReachedBottom && shouldShowGradients,
            );
          }
        };
        updateGradientVisibility();
        el.onscroll = updateGradientVisibility;
      },
      [individualContacts],
    );

    useEffect(() => {
      logDashTeamInvitePapEvent(PAP_Shown_DashInviteForm);
    }, [logDashTeamInvitePapEvent]);

    return (
      <Box className={styles.inviteFormBody}>
        {canInviteAllTeamMembers && teamMembers.length > 0 && (
          <>
            <InviteEntireTeam
              teamMembers={teamMembers}
              disabled={disabled || insufficientLicenses}
              insufficientLicensesTooltipTitle={
                insufficientLicenses
                  ? i18n.t("remove_emails_until_you_have_enough_licenses")
                  : undefined
              }
              onCheckboxToggle={handleToggleInviteAllCheckbox}
            />
            <Text variant="label" size="large" tagName="p" isBold>
              {i18n.t("team_invite_individuals_title")}
            </Text>
          </>
        )}

        <Box className={styles.inviteFormRowsWrapper}>
          <Box
            className={styles.inviteFormRowsContainer}
            ref={handleScrollContainerRef}
          >
            {individualContacts.map(({ contactId, ...contact }) => (
              <InviteFormRow
                className={styles.inviteFormRow}
                key={`invite-form-row-${contactId}`}
                showRoleSelect={canAssignRoles}
                disableRoleSelect={getShouldDisableRoleSelect(contact)}
                teamMembers={teamMembers}
                onChange={(contact) =>
                  handleInviteFormRowChange(contactId, contact)
                }
                onClose={() => handleRemoveRow(contactId)}
                preventClose={individualContacts.length === 1}
                emailValidationResult={contact.validationResult || undefined}
                disabled={
                  disabled ||
                  (!contact.email && licenseToBeUsed >= licenseCount)
                }
                isFssTeam={isFssTeam}
              />
            ))}
          </Box>
          <Box ref={topGradientRef} className={styles.topGradient} />
          <Box ref={bottomGradientRef} className={styles.bottomGradient} />
        </Box>

        <Box display="flex" flexDirection="row" alignItems="center">
          <Button
            variant="borderless"
            disabled={
              disabled ||
              licenseToBeUsed >= licenseCount ||
              licensesUsedWithEmptyTypeahead >= licenseCount
            }
            onClick={handleAddRow}
            withIconStart={<UIIcon src={AddLine} />}
            className={styles.addTypeaheadButton}
          >
            {i18n.t("team_invite_add_another")}
          </Button>
          <Box flexGrow={1}></Box>
          <Text
            size="xsmall"
            color={licenseToBeUsed >= licenseCount ? "error" : "subtle"}
          >
            {i18n.t("team_invite_of_x_licenses", {
              licensesUsed: licenseToBeUsed,
              licenseCount,
            })}
          </Text>
        </Box>

        {canInviteWithOptionalMessage && (
          <FormRow>
            <FormLabel
              htmlFor="invite-modal-optional-message"
              className={styles.optionalMessageLabel}
            >
              {i18n.t("optional_message")}
            </FormLabel>
            <TextArea
              id="invite-modal-optional-message"
              data-testid="optional-message-textarea"
              onChange={handleMessageChange}
              defaultValue={initialMessage}
              onFocus={handleTextAreaFocus}
              onBlur={handleTextAreaBlur}
              placeholder={
                isTextAreaFocused ? "" : i18n.t("optional_message_placeholder")
              }
              size="medium"
              className={styles.optionalMessageTextArea}
              disabled={disabled}
            />
          </FormRow>
        )}
      </Box>
    );
  },
);

InviteForm.displayName = "InviteForm";
